import { getStaffToken, staffSignedOut } from './staffSession';

const BASE = process.env.REACT_APP_API_URL || '';

async function request(path, options = {}) {
  const token = localStorage.getItem('ss_token');
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (res.status === 401 && !path.startsWith('/auth/')) {
    localStorage.removeItem('ss_token');
    window.location.href = '/login';
    return;
  }

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// ── Admin client-side cache ──
// Survives tab-switching within an admin session. Complements the Lambda-side
// in-memory cache: Lambda cache misses on cold starts or after a deploy;
// client cache survives those. Only GETs are cached.
const _adminCache = {};
const ADMIN_CACHE_TTL = 5 * 60 * 1000; // 5 min

function adminCacheKey(path) { return path; }
function getAdminCached(path) {
  const entry = _adminCache[adminCacheKey(path)];
  if (!entry) return null;
  if (Date.now() - entry.ts > ADMIN_CACHE_TTL) return null;
  return entry.data;
}
function setAdminCached(path, data) {
  _adminCache[adminCacheKey(path)] = { data, ts: Date.now() };
}
export function invalidateAdminClientCache(prefix) {
  if (!prefix) {
    for (const k of Object.keys(_adminCache)) delete _adminCache[k];
    return;
  }
  for (const k of Object.keys(_adminCache)) {
    if (k.startsWith(prefix)) delete _adminCache[k];
  }
}

// ── Admin API helper ──
// Prefers the staff sign-in (lib/staffSession.js) if present; falls back to the
// legacy ADMIN_KEY header (adminKey) for backwards compatibility during the
// per-user-login rollout.
//
// GET requests are cached in-memory for ADMIN_CACHE_TTL. Switching tabs or
// navigating back doesn't re-fetch if the data is still fresh. Writes are
// expected to call invalidateAdminClientCache() as needed; adminRequest
// auto-invalidates everything on any non-GET call (safe default — admin
// writes are low-frequency).
async function adminRequest(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const isGet = method === 'GET';

  // Opt-out: pass { noCache: true } to bypass (e.g. explicit refresh button)
  if (isGet && !options.noCache) {
    const cached = getAdminCached(path);
    if (cached) return cached;
  }

  const adminToken = getStaffToken('manage');
  const adminKey   = localStorage.getItem('adminKey') || '';
  const authHeaders = adminToken
    ? { Authorization: `Bearer ${adminToken}` }
    : { 'x-admin-key': adminKey };
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (res.status === 401) {
    // JWT is expired/invalid (or never present). Wipe the token + the
    // client-side admin cache so we don't keep returning stale data, and
    // send the browser to sign in — without this, an in-tab 401 leaves the
    // page looking logged-in with "Unauthorized" inline in each tab.
    invalidateAdminClientCache();
    staffSignedOut('manage');
    throw new Error('Unauthorized');
  }

  if (res.status === 403) {
    // The token is valid but its role can't reach this endpoint — the page
    // is stale about who's logged in (login switched in another tab, or the
    // role changed mid-session). Signal the Admin shell to re-fetch
    // /admin/me and re-render the tab set for the real role, instead of
    // leaving a raw "Forbidden" error inline.
    invalidateAdminClientCache();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ss-admin-forbidden'));
    }
  }

  // CSV exports return text, not JSON
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('text/csv')) {
    if (!res.ok) throw new Error('Export failed');
    return res.text();
  }

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');

  if (isGet) {
    setAdminCached(path, data);
  } else {
    // Any admin write might affect anything — bust the whole client cache
    // rather than maintain a per-route mapping. Admin writes are rare.
    invalidateAdminClientCache();
  }
  return data;
}

// SOS won't sync an invoice to QuickBooks when the PO Number is longer than
// this; the backend rejects anything over it too.
export const PO_NUMBER_MAX_LENGTH = 30;

export const api = {
  loginAsUser: (email, password) =>
    request('/auth/login', { method: 'POST', body: { email, password } }),

  getCustomer: () => request('/customer/me'),
  getDelivery: () => request('/customer/delivery'),

  getInvoices: () => request('/customer/invoices'),
  getInvoice: (id) => request(`/customer/invoices/${encodeURIComponent(id)}`),
  getInvoicePdf: (id) => request(`/customer/invoices/${encodeURIComponent(id)}/pdf`),
  getCreditMemo: (id) => request(`/customer/creditmemos/${encodeURIComponent(id)}`),
  getCreditMemoPdf: (id) => request(`/customer/creditmemos/${encodeURIComponent(id)}/pdf`),

  getProducts: (params = {}) =>
    request('/products?' + new URLSearchParams(params)),
  getCatalog: async () => {
    // Backend returns full catalog in one response (uses sosGetAllPages + cache)
    const result = await request('/products/catalog');
    if (!result) return result;
    if (result.totalPages <= 1) return result;

    // Fallback: parallel fetch remaining pages if backend returned paginated
    const remaining = [];
    for (let p = 2; p <= result.totalPages; p++) {
      remaining.push(request(`/products/catalog?page=${p}&pagesize=200`));
    }
    const results = await Promise.allSettled(remaining);
    const allProducts = [...result.products];
    for (const r of results) {
      if (r.status === 'fulfilled' && r.value?.products) {
        allProducts.push(...r.value.products);
      }
    }
    const catMap = {};
    allProducts.forEach(p => {
      const cat = p.category || 'Uncategorized';
      if (!catMap[cat]) catMap[cat] = { name: cat, subCategories: new Set(), count: 0 };
      catMap[cat].count++;
      if (p.subCategory) catMap[cat].subCategories.add(p.subCategory);
    });
    const categories = Object.values(catMap)
      .map(c => ({ ...c, subCategories: [...c.subCategories].sort() }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { products: allProducts, categories, total: allProducts.length, cached: false };
  },
  getOrderGuide: () => request('/products/order-guide'),
  // `exclude` is the list of sosIds already in the basket, so the checkout
  // cross-sell never suggests something the customer has already added.
  getRecommendations: ({ limit = 6, exclude = [] } = {}) =>
    request('/products/recommendations?' + new URLSearchParams({
      limit: String(limit),
      ...(exclude.length ? { exclude: exclude.join(',') } : {}),
    })),

  getOrders: (params = {}) =>
    request('/orders?' + new URLSearchParams(params)),
  placeOrder: (order) =>
    request('/orders', { method: 'POST', body: order }),
  updateOrder: (id, data) =>
    request(`/orders/${encodeURIComponent(id)}`, { method: 'PUT', body: data }),

  // The customer's own requests, so the portal can answer "did you get it?".
  getMyRequests: () => request('/customer/requests'),

  requestProduct: (data) =>
    request('/product-request', { method: 'POST', body: data }),

  sendFeedback: (data) =>
    request('/feedback', { method: 'POST', body: data }),

  sendServiceRequest: (data) =>
    request('/service-request', { method: 'POST', body: data }),

  // Public new-customer intake form (newcustomer.silverservice.com). Invite-only.
  verifyNewCustomerInvite: (token) =>
    request('/new-customer/verify?token=' + encodeURIComponent(token || '')),
  submitNewCustomer: (data) =>
    request('/new-customer', { method: 'POST', body: data }),

  requestPasswordReset: (email) =>
    request('/auth/request-reset', { method: 'POST', body: { email } }),

  // User management (customer_admin)
  listUsers: () => request('/users'),
  createUser: (data) => request('/users', { method: 'POST', body: data }),
  updateUser: (email, data) => request(`/users/${encodeURIComponent(email)}`, { method: 'PUT', body: data }),
  deleteUser: (email) => request(`/users/${encodeURIComponent(email)}`, { method: 'DELETE' }),
  resendInvite: (email) => request(`/users/${encodeURIComponent(email)}/resend-invite`, { method: 'POST' }),
  resetPassword: (email) => request(`/users/${encodeURIComponent(email)}/reset-password`, { method: 'POST' }),

  // Address book
  listAddresses:  () => request('/addresses'),
  createAddress:  (data) => request('/addresses', { method: 'POST', body: data }),
  updateAddress:  (id, data) => request(`/addresses/${encodeURIComponent(id)}`, { method: 'PUT', body: data }),
  deleteAddress:  (id) => request(`/addresses/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  setDefaultAddress: (id) => request(`/addresses/${encodeURIComponent(id)}/default`, { method: 'POST', body: {} }),

  // Pending orders (approval workflow)
  submitForApproval: (order) => request('/orders/pending', { method: 'POST', body: order }),
  getPendingOrders: () => request('/orders/pending'),
  approveOrder: (orderId) => request(`/orders/pending/${orderId}/approve`, { method: 'POST', body: {} }),
  rejectOrder: (orderId, reason) => request(`/orders/pending/${orderId}/reject`, { method: 'POST', body: { reason } }),
};

// ── Staff sign-in (staff.silverservice.com) ──
// Staff sign in once at the start page, which hands the sign-in to each site
// with a one-time code (lib/staffSession.js, backend/lib/staffHandoff.js).
async function staffAuthRequest(path, { method = 'GET', token, body } = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(data.error || 'Request failed');
    e.status = res.status;
    throw e;
  }
  return data;
}

export const staffAuthApi = {
  login: (email, password) => staffAuthRequest('/admin/login', { method: 'POST', body: { email, password } }),
  // Who the start page's sign-in belongs to, and which sites it opens.
  session: (token) => staffAuthRequest('/admin/session', { token }),
  handoff: (token, site) => staffAuthRequest('/admin/handoff', { method: 'POST', token, body: { site } }),
  redeem: (code, site) => staffAuthRequest('/admin/handoff/redeem', { method: 'POST', body: { code, site } }),
  // Signs this browser out of every staff site.
  logout: (token) => staffAuthRequest('/admin/logout', { method: 'POST', token, body: {} }),
};

// The Connect Claude screen (pages/ConnectClaude.jsx): the sign-in request
// Claude started, and the staff member's answer to it.
export const claudeConnectApi = {
  request: (id) => staffAuthRequest(`/oauth/request?id=${encodeURIComponent(id)}`),
  approve: (token, request) => staffAuthRequest('/oauth/approve', { method: 'POST', token, body: { request } }),
  deny: (request) => staffAuthRequest('/oauth/deny', { method: 'POST', body: { request } }),
};

// ── Admin API ──
export const adminApi = {
  me: (options = {}) => adminRequest('/admin/me', options),
  qboStatus: () => adminRequest('/admin/qbo/status'),
  qboAuthorizeUrl: () => adminRequest('/admin/qbo/authorize'),
  listRepresentatives: () => adminRequest('/admin/representatives'),
  addRepresentative: ({ name, email, role }) =>
    adminRequest('/admin/representatives', { method: 'POST', body: { name, email, role } }),
  setRepresentativeRole: (email, role) =>
    adminRequest(`/admin/representatives/${encodeURIComponent(email)}/role`, { method: 'POST', body: { role } }),
  setRepresentativeStatus: (email, status) =>
    adminRequest(`/admin/representatives/${encodeURIComponent(email)}/status`, { method: 'POST', body: { status } }),
  resendRepresentativeInvite: (email) =>
    adminRequest(`/admin/representatives/${encodeURIComponent(email)}/resend-invite`, { method: 'POST' }),

  getDashboard: (params = {}) =>
    adminRequest('/admin/dashboard?' + new URLSearchParams(params)),

  // Owner Metrics uses an async pattern — backend reads full window of
  // orders (~1-3 min) and caches in S3 per window. Same pattern as
  // Replenishment: GET reads cache, POST /refresh starts worker, GET
  // /status is the lightweight polling endpoint.
  getOwnerMetrics: (params = {}) =>
    adminRequest('/admin/owner-metrics?' + new URLSearchParams(params), { noCache: true }),
  getOwnerMetricsStatus: (params = {}) =>
    adminRequest('/admin/owner-metrics/status?' + new URLSearchParams(params), { noCache: true }),
  refreshOwnerMetrics: (params = {}) =>
    adminRequest('/admin/owner-metrics/refresh?' + new URLSearchParams(params), { method: 'POST', body: {} }),
  // Reports site (owners only): every rep's book rolled up.
  getSalesByRep: () => adminRequest('/admin/reports/sales-by-rep', { noCache: true }),
  // Reports site (owners only): every open QuickBooks bill, sorted by what it
  // really is. Read fresh from QuickBooks on each call.
  getOpenBills: () => adminRequest('/admin/reports/bills', { noCache: true }),

  // Customer crosswalk (sales BI unified customer master). GET reads the
  // cached merge; refresh re-pulls live SOS+HubSpot; confirm/reject persist a
  // human decision and return the freshly re-merged payload.
  getCrosswalk: () =>
    adminRequest('/admin/crosswalk', { noCache: true }),
  refreshCrosswalk: () =>
    adminRequest('/admin/crosswalk/refresh', { method: 'POST', body: {} }),
  confirmCrosswalkMatch: (data) =>
    adminRequest('/admin/crosswalk/confirm', { method: 'POST', body: data }),
  rejectCrosswalkMatch: (data) =>
    adminRequest('/admin/crosswalk/reject', { method: 'POST', body: data }),

  getOrders: (params = {}) =>
    adminRequest('/admin/orders?' + new URLSearchParams(params)),

  getOrderAudit: (params = {}) =>
    adminRequest('/admin/order-audit?' + new URLSearchParams(params), { noCache: true }),

  getCustomerActivity: (params = {}) =>
    adminRequest('/admin/customers/activity?' + new URLSearchParams(params)),

  getCustomerOrders: (customerId, params = {}) =>
    adminRequest(`/admin/customers/${customerId}/orders?` + new URLSearchParams(params)),

  // Staff order entry: place (or dry-run price) an order on the customer's
  // behalf — docs/admin-order-entry.md.
  placeCustomerOrder: (customerId, data) =>
    adminRequest(`/admin/customers/${customerId}/orders`, { method: 'POST', body: data }),
  getCustomerAddresses: (customerId) =>
    adminRequest(`/admin/customers/${customerId}/addresses`),

  // Customer Synopsis: insights + recent orders + staff notes + cached AI blurb.
  getCustomerSynopsis: (customerId) =>
    adminRequest(`/admin/customers/${customerId}/synopsis`),

  addCustomerNote: (customerId, data) =>
    adminRequest(`/admin/customers/${customerId}/notes`, { method: 'POST', body: data }),

  deleteCustomerNote: (customerId, noteId) =>
    adminRequest(`/admin/customers/${customerId}/notes/${encodeURIComponent(noteId)}`, { method: 'DELETE' }),

  listCustomers: (params = {}) =>
    adminRequest('/admin/customers?' + new URLSearchParams(params)),

  sendInvite: (data) =>
    adminRequest('/admin/invite', { method: 'POST', body: data }),

  // ── New-customer applications (online intake review + SOS provisioning) ──
  // Invite links that gate the public form
  createNewCustomerInvite: (data) =>
    adminRequest('/admin/new-customer-invites', { method: 'POST', body: data }),
  listNewCustomerInvites: () =>
    adminRequest('/admin/new-customer-invites', { noCache: true }),
  revokeNewCustomerInvite: (token) =>
    adminRequest(`/admin/new-customer-invites/${encodeURIComponent(token)}/revoke`, { method: 'POST', body: {} }),

  listRequests: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v)
    ).toString();
    return adminRequest(`/admin/requests${qs ? `?${qs}` : ''}`, { noCache: true });
  },
  setRequestStatus: (id, status) =>
    adminRequest(`/admin/requests/${encodeURIComponent(id)}/status`, { method: 'POST', body: { status } }),

  listNewCustomers: () =>
    adminRequest('/admin/new-customers', { noCache: true }),
  newCustomerCertificateUrl: (id) =>
    adminRequest(`/admin/new-customers/${encodeURIComponent(id)}/certificate`, { noCache: true }),
  provisionNewCustomerSos: (id, data) =>
    adminRequest(`/admin/new-customers/${encodeURIComponent(id)}/provision-sos`, { method: 'POST', body: data }),
  dismissNewCustomer: (id) =>
    adminRequest(`/admin/new-customers/${encodeURIComponent(id)}/dismiss`, { method: 'POST', body: {} }),

  exportOrders: (params = {}) =>
    adminRequest('/admin/export/orders?' + new URLSearchParams(params)),

  exportCustomers: (params = {}) =>
    adminRequest('/admin/export/customers?' + new URLSearchParams(params)),

  exportContacts: () =>
    adminRequest('/admin/export/contacts', { noCache: true }),

  // Structured delivery schedule (Phase 1 slice A) — portal-owned; replaces
  // guessing the delivery day from SOS notes.
  getCustomerDetails: (customerId) =>
    adminRequest(`/admin/customers/${customerId}/details`, { noCache: true }),

  setCustomerDetails: (customerId, data) =>
    // Saved through to SOS; dryRun returns the preview and writes nothing.
    adminRequest(`/admin/customers/${customerId}/details`, { method: 'POST', body: data }),

  getDeliverySettings: (customerId) =>
    adminRequest(`/admin/customers/${customerId}/delivery-settings`),
  setDeliverySettings: (customerId, data) =>
    adminRequest(`/admin/customers/${customerId}/delivery-settings`, { method: 'POST', body: data }),

  setAdminEmail: (customerId, email) =>
    adminRequest(`/admin/customers/${customerId}/admin-email`, { method: 'POST', body: { email } }),

  getOrderGuide: (customerId) =>
    adminRequest(`/admin/customers/${customerId}/order-guide`),

  setOrderGuide: (customerId, items, hiddenItems) =>
    adminRequest(`/admin/customers/${customerId}/order-guide`, { method: 'POST', body: { items, hiddenItems } }),

  // Portal-owned tier pricing (Phase 1 slice C).
  getPricing: () => adminRequest('/admin/pricing', { noCache: true }),
  importPricingFromSos: () => adminRequest('/admin/pricing/import', { method: 'POST' }),
  getPricingSosDiff: () => adminRequest('/admin/pricing/sos-diff', { noCache: true }),
  setTierPrice: (tierId, itemId, price, reason) =>
    adminRequest(`/admin/pricing/${tierId}/${itemId}`, { method: 'POST', body: { price, reason } }),
  removeTierPrice: (tierId, itemId, reason) =>
    adminRequest(`/admin/pricing/${tierId}/${itemId}`, { method: 'DELETE', body: { reason } }),
  addTierItemOnRule: (tierId, itemId) =>
    adminRequest(`/admin/pricing/${tierId}/${itemId}`, { method: 'POST', body: { follow: true, reason: 'Added on the tier rule' } }),
  unpinTierPrice: (tierId, itemId) =>
    adminRequest(`/admin/pricing/${tierId}/${itemId}/unpin`, { method: 'POST' }),
  // mode: 'fixed' | 'percent'. dryRun previews every price that would move.
  setTierMode: (tierId, { mode, percentOff, dryRun, reason }) =>
    adminRequest(`/admin/pricing/tiers/${tierId}/mode`, { method: 'POST', body: { mode, percentOff, dryRun, reason } }),
  // The portal-pricing toggle. dryRun returns every customer price that moves.
  setPricingLive: (live, { dryRun, reason } = {}) =>
    adminRequest('/admin/pricing/live', { method: 'POST', body: { live, dryRun, reason } }),
  getTierPriceHistory: (tierId, itemId) =>
    adminRequest(`/admin/pricing/${tierId}/${itemId}/history`, { noCache: true }),

  getCatalog: (params = {}) =>
    adminRequest('/admin/catalog?' + new URLSearchParams(params)),

  setProductVisibility: (sku, visible) =>
    // visible: true | false | null. null resets to "pending review".
    adminRequest(`/admin/products/${encodeURIComponent(sku)}/visibility`, {
      method: 'POST',
      body: { visible },
    }),

  setProductCategory: (sku, { category, subCategory } = {}) =>
    // category / subCategory: string to override, null to clear and fall
    // back to the SOS-parsed value, undefined/absent to leave alone.
    adminRequest(`/admin/products/${encodeURIComponent(sku)}/category`, {
      method: 'POST',
      body: { category, subCategory },
    }),

  setProductBrand: (sku, brand) =>
    // brand: string to override, null/'' to clear and fall back to the
    // SOS Brand custom-field value (run through the alias normalizer).
    adminRequest(`/admin/products/${encodeURIComponent(sku)}/brand`, {
      method: 'POST',
      body: { brand },
    }),

  setProductText: (sku, { name, description } = {}) =>
    // name / description: string to override, null/'' to clear and fall
    // back to the SOS value, undefined/absent to leave alone.
    adminRequest(`/admin/products/${encodeURIComponent(sku)}/text`, {
      method: 'POST',
      body: { name, description },
    }),

  setItemPrice: (sku, { price, taxable, reason, dryRun } = {}) =>
    // Saved through to SOS (which passes prices on to QuickBooks). Send only
    // the fields being changed; leave price/taxable undefined otherwise. dryRun
    // returns the preview — who pays what — and writes nothing.
    adminRequest(`/admin/pricing/items/${encodeURIComponent(sku)}`, {
      method: 'POST',
      body: { price, taxable, reason, dryRun },
    }),

  uploadProductImage: (sku, dataUrl) =>
    // dataUrl: a `data:image/jpeg;base64,...` produced by Canvas
    // toDataURL on the client (frontend resize already happened). Server
    // validates + uploads to S3 + flips hasImage on the live catalog.
    adminRequest(`/admin/products/${encodeURIComponent(sku)}/image`, {
      method: 'POST',
      body: { image: dataUrl },
    }),

  deleteProductImage: (sku) =>
    // Removes the S3 object + flips hasImage:false on the live catalog.
    // Idempotent — calling on a product with no image is a no-op.
    adminRequest(`/admin/products/${encodeURIComponent(sku)}/image`, {
      method: 'DELETE',
    }),

  migrateCatalogVisibility: () =>
    adminRequest('/admin/catalog/migrate-visibility', { method: 'POST', body: {} }),

  syncCatalog: () =>
    adminRequest('/admin/sync-catalog', { method: 'POST', body: {} }),

  syncCatalogStatus: () =>
    adminRequest('/admin/sync-catalog/status', { noCache: true }),

  // ── Replenishment Planner ──
  // Async pattern: refresh kicks off a background worker; UI polls status
  // until done. Computation takes ~2 min — too long for API Gateway sync.
  getReplenishment: () =>
    adminRequest('/admin/replenishment', { noCache: true }),

  getReplenishmentStatus: () =>
    adminRequest('/admin/replenishment/status', { noCache: true }),

  refreshReplenishment: () =>
    adminRequest('/admin/replenishment/refresh', { method: 'POST', body: {} }),

  saveReplenishmentOverride: (itemId, data) =>
    adminRequest(`/admin/replenishment/${encodeURIComponent(itemId)}/override`, { method: 'POST', body: data }),

  // overrides: [{ itemId, leadTimeDays?, actualPO?, notes? }, …] — used for
  // brand-wide lead-time updates and recording basket quantities as Actual PO.
  bulkReplenishmentOverride: (overrides) =>
    adminRequest('/admin/replenishment/bulk-override', { method: 'POST', body: { overrides } }),

  // lines: [{ itemId, qty }, …] — creates one purchase order per vendor in
  // SOS (a reviewable document; nothing is sent to the vendor). Costs and
  // vendors are re-derived server-side; outside prod the backend redirects
  // every PO to the designated test vendor.
  createReplenishmentPOs: (lines) =>
    adminRequest('/admin/replenishment/create-pos', { method: 'POST', body: { lines } }),

  exportReplenishment: () =>
    adminRequest('/admin/replenishment/export', { noCache: true }),

  // ── Expense receipts (admin side) ──
  importAmex: (csv) =>
    adminRequest('/admin/amex-import', { method: 'POST', body: { csv } }),
  reconcileReceipts: (params = {}) =>
    adminRequest('/admin/receipts/reconcile?' + new URLSearchParams(params), { noCache: true }),
  // CSV report — adminRequest returns text for text/csv responses.
  receiptReport: (params = {}) =>
    adminRequest('/admin/receipts/report?' + new URLSearchParams(params), { noCache: true }),
  listReceipts: (params = {}) =>
    adminRequest('/admin/receipts?' + new URLSearchParams(params), { noCache: true }),
  getReceiptPhoto: (id) =>
    adminRequest(`/admin/receipts/${encodeURIComponent(id)}/photo`, { noCache: true }),
  setReceiptReason: (id, reason) =>
    adminRequest(`/admin/receipts/${encodeURIComponent(id)}/reason`, { method: 'POST', body: { reason } }),
  matchReceipt: (id, txnId) =>
    adminRequest(`/admin/receipts/${encodeURIComponent(id)}/match`, { method: 'POST', body: { txnId } }),
  // ── Specials / sale pricing ──
  // Draft → publish workflow. setSpecial writes a draft (invisible to
  // customers); publish makes it live. data shape:
  //   { discountType: 'percent'|'amount', discountValue, audience, startDate?, endDate?, label? }
  //   audience: { type:'all' } | { type:'customers', customerIds:[...] }
  listSpecials: () =>
    adminRequest('/admin/specials', { noCache: true }),
  setSpecial: (sku, data) =>
    adminRequest(`/admin/specials/${encodeURIComponent(sku)}`, { method: 'POST', body: data }),
  publishSpecial: (sku) =>
    adminRequest(`/admin/specials/${encodeURIComponent(sku)}/publish`, { method: 'POST', body: {} }),
  publishAllSpecials: () =>
    adminRequest('/admin/specials/publish-all', { method: 'POST', body: {} }),
  unpublishSpecial: (sku) =>
    adminRequest(`/admin/specials/${encodeURIComponent(sku)}/unpublish`, { method: 'POST', body: {} }),
  deleteSpecial: (sku) =>
    adminRequest(`/admin/specials/${encodeURIComponent(sku)}`, { method: 'DELETE' }),

  listCapturers: () =>
    adminRequest('/admin/capturers', { noCache: true }),
  createCapturer: (data) =>
    adminRequest('/admin/capturers', { method: 'POST', body: data }),
  deleteCapturer: (email) =>
    adminRequest(`/admin/capturers/${encodeURIComponent(email)}`, { method: 'DELETE' }),
};

// ── Staff sites' own APIs ──
// Each staff site sends its own copy of the staff sign-in. A 401 means that
// sign-in is gone; staffSignedOut clears it and useStaffSession sends the
// browser back to the start page.
function staffRequest(site) {
  return async (path, options = {}) => {
    const token = getStaffToken(site);
    const res = await fetch(`${BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
    if (res.status === 401) {
      staffSignedOut(site);
      throw new Error('Unauthorized');
    }
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  };
}

// ── Receipt capture surface ──
// Field staff (receipt_capturer role) use the same staff sign-in as owners, but
// the /admin/* routes reject their role server-side; they only reach /receipts/*.
const captureRequest = staffRequest('receipts');

export const captureApi = {
  // Reads the photo to pre-fill the form. Never saves — capture() still does
  // that, with whatever the capturer confirmed.
  extract: (image) => captureRequest('/receipts/extract', { method: 'POST', body: { image } }),
  capture: (data) => captureRequest('/receipts/capture', { method: 'POST', body: data }),
  mine: () => captureRequest('/receipts/mine'),
};

// ── Staff preview of the ordering site ──
// Ordering's narrow staff sign-in can only list customers and start a
// look-only preview as one of them (backend lib/staffPreview.js).
const previewRequest = staffRequest('orders');

export const previewApi = {
  customers: () => previewRequest('/preview/customers'),
  start: (customerId) => previewRequest('/preview/start', { method: 'POST', body: { customerId } }),
};

// ── Sales tool (sales.silverservice.com) ──
// The rep's AdminUsers record carries role 'sales_rep' (owners and admins see
// every account).
const salesRequest = staffRequest('sales');

export const salesApi = {
  me: () => salesRequest('/sales/me'),
  getAccounts: () => salesRequest('/sales/accounts'),
  getAccount: (key) => salesRequest('/sales/account?key=' + encodeURIComponent(key)),
  getAccountOrders: (key, range) => salesRequest('/sales/account/orders?' + new URLSearchParams({ key, ...range })),
  refreshMetrics: () => salesRequest('/sales/metrics/refresh', { method: 'POST', body: {} }),
  getMetricsStatus: () => salesRequest('/sales/metrics/status'),
  getScorecard: () => salesRequest('/sales/scorecard'),
  setBaseline: (data) => salesRequest('/sales/baseline', { method: 'POST', body: data }),
  setTarget: (targetPct) => salesRequest('/sales/baseline/target', { method: 'POST', body: { targetPct } }),
  getLeads: () => salesRequest('/sales/leads'),
  updateLead: (leadId, changes) => salesRequest('/sales/leads/' + encodeURIComponent(leadId), { method: 'POST', body: changes }),
  getAccountActivity: (key) => salesRequest('/sales/account/activity?key=' + encodeURIComponent(key)),
  logActivity: (entry) => salesRequest('/sales/account/activity', { method: 'POST', body: entry }),
  createTask: (task) => salesRequest('/sales/account/tasks', { method: 'POST', body: task }),
  updateTask: (id, changes) => salesRequest('/sales/tasks/' + encodeURIComponent(id), { method: 'POST', body: changes }),
  getMyTasks: () => salesRequest('/sales/tasks/mine'),
  getOwners: () => salesRequest('/sales/owners'),
  setAccountOwner: (key, ownerId) => salesRequest('/sales/account/owner', { method: 'POST', body: { key, ownerId } }),
  getWinback: () => salesRequest('/sales/winback'),
  getSynopsis: (key, customerId) => salesRequest(`/sales/account/synopsis?key=${encodeURIComponent(key)}&customerId=${encodeURIComponent(customerId)}`),
  addSynopsisNote: (key, customerId, note) => salesRequest('/sales/account/notes', { method: 'POST', body: { key, customerId, ...note } }),
  deleteSynopsisNote: (key, customerId, noteId) => salesRequest(`/sales/account/notes?key=${encodeURIComponent(key)}&customerId=${encodeURIComponent(customerId)}&noteId=${encodeURIComponent(noteId)}`, { method: 'DELETE' }),
};

// ── Service desk (service.silverservice.com) ──
const serviceRequest = staffRequest('service');

const enc = encodeURIComponent;
const query = (params = {}) => {
  const q = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => `${enc(k)}=${enc(v)}`).join('&');
  return q ? `?${q}` : '';
};

export const serviceApi = {
  me: () => serviceRequest('/service/me'),
  staff: () => serviceRequest('/service/staff'),
  customers: () => serviceRequest('/service/customers'),
  jobs: (params) => serviceRequest('/service/jobs' + query(params)),
  tracker: () => serviceRequest('/service/tracker'),
  findInvoice: (number) => serviceRequest(`/service/invoices/${enc(number)}`),
  setBilling: (id, change) => serviceRequest(`/service/jobs/${enc(id)}/billing`, { method: 'POST', body: change }),
  job: (id) => serviceRequest(`/service/jobs/${enc(id)}`),
  createJob: (data) => serviceRequest('/service/jobs', { method: 'POST', body: data }),
  updateJob: (id, patch) => serviceRequest(`/service/jobs/${enc(id)}`, { method: 'POST', body: patch }),
  addNote: (id, text) => serviceRequest(`/service/jobs/${enc(id)}/notes`, { method: 'POST', body: { text } }),
  addPhoto: (id, image) => serviceRequest(`/service/jobs/${enc(id)}/photos`, { method: 'POST', body: { image } }),
  requests: () => serviceRequest('/service/requests'),
  requestToJob: (id, data) => serviceRequest(`/service/requests/${enc(id)}/job`, { method: 'POST', body: data || {} }),
  closeRequest: (id) => serviceRequest(`/service/requests/${enc(id)}/close`, { method: 'POST', body: {} }),
  assets: (params) => serviceRequest('/service/assets' + query(params)),
  asset: (id) => serviceRequest(`/service/assets/${enc(id)}`),
  createAsset: (data) => serviceRequest('/service/assets', { method: 'POST', body: data }),
  updateAsset: (id, patch) => serviceRequest(`/service/assets/${enc(id)}`, { method: 'POST', body: patch }),
  addFollowUp: (id, data) => serviceRequest(`/service/jobs/${enc(id)}/followups`, { method: 'POST', body: data }),
  setFollowUpDone: (id, followUpId, done) => serviceRequest(`/service/jobs/${enc(id)}/followups/${enc(followUpId)}`, { method: 'POST', body: { done } }),
  attachChecklist: (id, templateId) => serviceRequest(`/service/jobs/${enc(id)}/checklists`, { method: 'POST', body: { templateId } }),
  setChecklistItem: (id, templateId, itemId, value) => serviceRequest(`/service/jobs/${enc(id)}/checklists/${enc(templateId)}`, { method: 'POST', body: { itemId, value } }),
  addChecklistPhoto: (id, templateId, itemId, image) => serviceRequest(`/service/jobs/${enc(id)}/checklists/${enc(templateId)}/photo`, { method: 'POST', body: { itemId, image } }),
  schedules: () => serviceRequest('/service/schedules'),
  saveSchedule: (id, data) => serviceRequest(id ? `/service/schedules/${enc(id)}` : '/service/schedules', { method: 'POST', body: data }),
  runSchedules: () => serviceRequest('/service/schedules/run', { method: 'POST', body: {} }),
  checklists: () => serviceRequest('/service/checklists'),
  trends: (months) => serviceRequest('/service/trends' + query({ months })),
  saveChecklist: (id, data) => serviceRequest(id ? `/service/checklists/${enc(id)}` : '/service/checklists', { method: 'POST', body: data }),
  parts: () => serviceRequest('/service/parts'),
  savePart: (id, data) => serviceRequest(id ? `/service/parts/${enc(id)}` : '/service/parts', { method: 'POST', body: data }),
  addActionItem: (id, data) => serviceRequest(`/service/jobs/${enc(id)}/actions`, { method: 'POST', body: data }),
  setActionItemDone: (id, itemId, done) => serviceRequest(`/service/jobs/${enc(id)}/actions/${enc(itemId)}`, { method: 'POST', body: { done } }),
  askForFeedback: (id, to) => serviceRequest(`/service/jobs/${enc(id)}/feedback`, { method: 'POST', body: { to } }),
};

// The customer's feedback page for a service visit. Public: the signed link
// from the email is the only credential.
export const serviceFeedbackApi = {
  get: (t) => request('/service-feedback?t=' + encodeURIComponent(t || '')),
  send: (t, answer) => request('/service-feedback', { method: 'POST', body: { t, ...answer } }),
};

// ── Fulfillment (fulfillment.silverservice.com) ──
const fulfillmentRequest = staffRequest('fulfillment');

export const fulfillmentApi = {
  me: () => fulfillmentRequest('/fulfillment/me'),
  board: () => fulfillmentRequest('/fulfillment/board'),
  order: (id) => fulfillmentRequest(`/fulfillment/orders/${enc(id)}`),
  recordDelivery: (id, data) => fulfillmentRequest(`/fulfillment/orders/${enc(id)}/delivery`, { method: 'POST', body: data }),
  addDeliveryPhoto: (id, image) => fulfillmentRequest(`/fulfillment/orders/${enc(id)}/delivery/photo`, { method: 'POST', body: { image } }),
};
